import { useState, useEffect, useRef } from 'react';
import styled from 'foundations-theming/utils';
import { useIconSize } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCheckboxContainer = styled.div.withConfig({
  displayName: "Checkbox__StyledCheckboxContainer",
  componentId: "sc-1rbz5my-0"
})(() => {
  return {
    display: 'inline-block',
    position: 'relative'
  };
});
const StyledHiddenCheckbox = styled.input.withConfig({
  displayName: "Checkbox__StyledHiddenCheckbox",
  componentId: "sc-1rbz5my-1"
})(() => {
  return {
    position: 'absolute',
    opacity: '0',
    cursor: 'pointer',
    blockSize: '0px',
    inlineSize: '0px'
  };
});
const StyledCheckbox = styled.div.withConfig({
  displayName: "Checkbox__StyledCheckbox",
  componentId: "sc-1rbz5my-2"
})(({
  theme,
  $checked,
  $disabled,
  $indeterminate
}) => {
  const checkedColor = $checked || $indeterminate ? theme.color['border-interactive-default'] : theme.color['border-core-default'];
  return Object.assign({
    width: '16px',
    height: '16px',
    borderRadius: theme.borderRadius[200],
    border: `${theme.space[25]} solid ${checkedColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 150ms ease-out',
    backgroundColor: theme.color['fill-surface-default'],
    color: checkedColor,
    outline: 0
  }, $disabled && {
    cursor: 'not-allowed'
  }, !$disabled && {
    '&:hover': {
      backgroundColor: theme.color['fill-tertiary-default']
    }
  }, {
    '&:focus-visible:not(:disabled)': {
      boxShadow: theme.boxShadow[100]
    }
  });
});
const StyledIconWrapper = styled.div.withConfig({
  displayName: "Checkbox__StyledIconWrapper",
  componentId: "sc-1rbz5my-3"
})(({
  $visible,
  $size
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    inset: 0,
    transition: 'all 150ms ease-out',
    opacity: $visible ? 1 : 0,
    '& svg': {
      width: $size,
      height: $size,
      fill: 'currentColor',
      transition: 'all 150ms ease-out'
    }
  };
});
export const Checkbox = ({
  id,
  checked: checkedProp,
  defaultChecked = false,
  indeterminate = false,
  disabled = false,
  onChange
}) => {
  const checkboxRef = useRef(null);
  const isControlled = checkedProp !== undefined;
  const [internalIsChecked, setInternalIsChecked] = useState(defaultChecked);
  const isChecked = isControlled ? checkedProp : internalIsChecked;
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  const manageDisabled = event => {
    if (disabled) {
      event.preventDefault();
      return;
    }
  };
  const handleChange = event => {
    manageDisabled(event);
    const newChecked = !internalIsChecked;
    if (!isControlled) {
      setInternalIsChecked(indeterminate ? true : newChecked);
    }
    onChange === null || onChange === void 0 || onChange(newChecked);
  };
  const handleKeyDown = event => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleChange(event);
    }
  };
  const iconSize = useIconSize('200');
  return /*#__PURE__*/_jsxs(StyledCheckboxContainer, {
    children: [/*#__PURE__*/_jsx(StyledHiddenCheckbox, {
      id: id,
      ref: checkboxRef,
      type: "checkbox",
      checked: isChecked,
      disabled: disabled,
      onChange: handleChange
    }), /*#__PURE__*/_jsxs(StyledCheckbox, {
      $checked: isChecked,
      $disabled: disabled,
      $indeterminate: indeterminate,
      tabIndex: 0,
      role: "checkbox",
      "aria-checked": indeterminate ? 'mixed' : isChecked,
      onKeyDown: handleKeyDown,
      onClick: handleChange,
      children: [/*#__PURE__*/_jsx(StyledIconWrapper, {
        $visible: indeterminate,
        $size: iconSize,
        children: /*#__PURE__*/_jsx("svg", {
          role: "presentation",
          viewBox: "0 0 14 14",
          xmlns: "http://www.w3.org/2000/svg",
          children: /*#__PURE__*/_jsx("path", {
            d: "M 2 6 L12 6 L12 9 L2 9 L2 6 z"
          })
        })
      }), /*#__PURE__*/_jsx(StyledIconWrapper, {
        $visible: isChecked && !indeterminate,
        $size: iconSize,
        children: /*#__PURE__*/_jsx("svg", {
          role: "presentation",
          viewBox: "0 0 15 15.343",
          xmlns: "http://www.w3.org/2000/svg",
          children: /*#__PURE__*/_jsx("path", {
            d: "M1.013 8.11c0-.223.078-.412.234-.568l1.14-1.14c.155-.155.345-.233.568-.233s.413.077.57.233l2.46 2.47 5.492-5.5c.156-.156.346-.234.568-.234.224 0 .413.077.57.233l1.138 1.14c.156.155.234.345.234.568 0 .224-.078.414-.234.57l-6.06 6.06-1.14 1.14c-.155.155-.345.233-.568.233s-.413-.078-.57-.234l-1.138-1.14-3.03-3.03c-.156-.156-.234-.346-.234-.57z"
          })
        })
      })]
    })]
  });
};